import { createElement } from 'react';
import {
  functionalUpdate,
  type ColumnDef,
  type OnChangeFn,
  type RowData,
  type SortingState,
} from '@/lib/tanstack-table';
import { MAX_THEN_SORT_LEVELS, type SortDirection } from '@erp/rpc';
import { DataTableColumnHeader } from './data-table-column-header';
import { isUnsortableColumn, type UnsortableColumn } from './unsortable-column';

export interface ServerSortLevel<TSortBy extends string> {
  sortBy: TSortBy;
  sortDirection: SortDirection;
}

/**
 * A server-sorted table's sort as its URL, saved view and list query carry
 * it: the primary column, then the contract's `thenSort` tie-breakers.
 */
export interface ServerSort<TSortBy extends string> {
  sortBy?: TSortBy;
  sortDirection?: SortDirection;
  thenSort?: ServerSortLevel<TSortBy>[];
}

/** A sort change, every key present so a cleared sort clears the URL. */
export interface ServerSortUpdate<TSortBy extends string> {
  sortBy: TSortBy | undefined;
  sortDirection: SortDirection | undefined;
  thenSort: ServerSortLevel<TSortBy>[] | undefined;
}

/** Most columns a server-sorted table sorts by at once. */
export const MAX_SERVER_SORT_LEVELS = 1 + MAX_THEN_SORT_LEVELS;

export function sortingFromServerSort<TSortBy extends string>({
  sortBy,
  sortDirection,
  thenSort = [],
}: ServerSort<TSortBy>): SortingState {
  if (!sortBy || !sortDirection) return [];
  return [{ sortBy, sortDirection }, ...thenSort].map((level) => ({
    id: level.sortBy,
    desc: level.sortDirection === 'desc',
  }));
}

/**
 * The server sort for a table's sorting state, every key present so a cleared
 * sort clears the URL. Columns `toSortBy` doesn't know are dropped, and levels
 * past {@link MAX_SERVER_SORT_LEVELS} are refused: a shift-click that would
 * add one more leaves the sort as it was. (The table's own
 * `maxMultiSortColCount` would drop the primary level to make room instead.)
 */
export function serverSortFromSorting<TSortBy extends string>(
  sorting: SortingState,
  toSortBy: (columnId: string) => TSortBy | undefined,
): ServerSortUpdate<TSortBy> {
  const levels = sorting
    .slice(0, MAX_SERVER_SORT_LEVELS)
    .flatMap((sort): ServerSortLevel<TSortBy>[] => {
      const sortBy = toSortBy(sort.id);
      return sortBy
        ? [{ sortBy, sortDirection: sort.desc ? 'desc' : 'asc' }]
        : [];
    });
  const [primary, ...thenSort] = levels;
  return {
    sortBy: primary?.sortBy,
    sortDirection: primary?.sortDirection,
    thenSort: thenSort.length > 0 ? thenSort : undefined,
  };
}

/**
 * A column of a server-sorted table: its `id` is one of the list contract's
 * sort keys, so the server can order by it, unless it opted out through
 * {@link unsortableColumn}. Adding a column therefore means adding its key to
 * the contract's sort enum, which in turn needs its SQL in the backend's
 * `buildListOrder` columns.
 */
export type ServerSortedColumn<TData extends RowData, TSortBy extends string> =
  | (ColumnDef<TData> & { id: TSortBy; enableSorting?: never })
  | UnsortableColumn<TData>;

/**
 * Sorting for a table whose rows the server sorts: every column not opted out
 * sorts (a plain-text `header` becomes a clickable sort header), shift-click
 * adds a sort level, and the sort round-trips through the caller's state
 * (usually the URL) as `sortBy`/`sortDirection`/`thenSort`.
 *
 * Spread `tableOptions` into `useTable`, pass `sorting` as `state.sorting`,
 * and use the returned `columns`.
 */
export function useServerSort<TData extends RowData, TSortBy extends string>({
  columns,
  toSortBy,
  sort,
  onSortChange,
}: {
  columns: readonly ServerSortedColumn<TData, TSortBy>[];
  /** The contract sort key for a column id, if the server can sort by it. */
  toSortBy: (columnId: string) => TSortBy | undefined;
  sort: ServerSort<TSortBy>;
  onSortChange: (next: ServerSortUpdate<TSortBy>) => void;
}) {
  const sorting = sortingFromServerSort(sort);
  const sortableColumns = columns.map((column): ColumnDef<TData> => {
    if (isUnsortableColumn(column)) return column;
    const { header } = column;
    return {
      ...column,
      enableSorting: true,
      // A plain-text title still gets the clickable sort header.
      ...(typeof header === 'string'
        ? {
            header: ({ column: tableColumn }) =>
              createElement(DataTableColumnHeader<TData, unknown>, {
                column: tableColumn,
                title: header,
              }),
          }
        : {}),
    };
  });
  const onSortingChange: OnChangeFn<SortingState> = (updater) =>
    onSortChange(
      serverSortFromSorting(functionalUpdate(updater, sorting), toSortBy),
    );
  return {
    columns: sortableColumns,
    sorting,
    tableOptions: {
      manualSorting: true,
      // Shift-click a header to add it as the next sort level.
      enableMultiSort: true,
      onSortingChange,
    },
  } as const;
}
